// Turn a blog category name into its CSS color class.
function blogCatClass(name) {
  if (!name) return 'blog-cat-default';
  const slug = name.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const known = ['technology','web-development','python','cyber-security','ai-ml','general'];
  return known.includes(slug) ? `blog-cat-${slug}` : 'blog-cat-default';
}

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Icon from 'components/Icon';
import { blogAPI } from 'utils/api';

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    blogAPI.getBySlug(slug)
      .then(r => setBlog(r.data))
      .catch(() => setBlog(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Update document title for SEO
  useEffect(() => {
    if (blog) {
      document.title = `${blog.metaTitle || blog.title} — Devlayar`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.content = blog.metaDescription || blog.excerpt || '';
    }
    return () => { document.title = 'Devlayar — Free Online PDF & Image Tools'; };
  }, [blog]);

  if (loading) return <div className="loading" style={{ padding:120 }}><div className="spinner" />Loading article...</div>;

  if (!blog) return (
    <div className="not-found">
      <h1>404</h1><h2>Article Not Found</h2>
      <p>This article does not exist or has been removed.</p>
      <Link to="/blog" className="btn btn-primary"><Icon name="arrowLeft" size={16} />Back to Blog</Link>
    </div>
  );

  return (
    <div style={{ minHeight:'100vh', background:'var(--white)' }}>
      {/* Full-width header */}
      <div style={{ background:'linear-gradient(160deg,var(--blue-50),#e8f0fe)', borderBottom:'1px solid var(--blue-100)', padding:'56px 0 48px' }}>
        <div className="container" style={{ maxWidth:820 }}>
          <Link to="/blog" style={{ display:'inline-flex', alignItems:'center', gap:7, color:'var(--slate-500)', fontSize:14, fontWeight:500, marginBottom:24, transition:'color var(--ease)' }}
            onMouseEnter={e=>e.currentTarget.style.color='var(--blue)'} onMouseLeave={e=>e.currentTarget.style.color='var(--slate-500)'}>
            <Icon name="arrowLeft" size={16} />Back to Blog
          </Link>
          <span className={`cat-badge ${blogCatClass(blog.category)}`} style={{ marginBottom:14, display:'inline-flex' }}>{blog.category}</span>
          <h1 style={{ fontSize:'clamp(26px,4vw,40px)', fontWeight:800, letterSpacing:-1, lineHeight:1.2, color:'var(--slate-900)', margin:'10px 0 18px' }}>{blog.title}</h1>
          <div style={{ display:'flex', alignItems:'center', gap:16, flexWrap:'wrap', color:'var(--slate-400)', fontSize:13 }}>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><Icon name="calendar" size={14} />{new Date(blog.createdAt).toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</span>
            <span style={{ display:'flex', alignItems:'center', gap:5 }}><Icon name="eye" size={14} />{blog.views} views</span>
            {blog.tags?.length > 0 && (
              <span style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                <Icon name="tag" size={14} />
                {blog.tags.map(tag => (
                  <span key={tag} style={{ background:'var(--white)', border:'1px solid var(--slate-200)', padding:'2px 10px', borderRadius:'var(--r-full)', fontSize:12, fontWeight:500, color:'var(--slate-600)' }}>{tag}</span>
                ))}
              </span>
            )}
          </div>
          {blog.excerpt && (
            <p style={{ fontSize:17, color:'var(--slate-500)', lineHeight:1.65, fontStyle:'italic', borderLeft:'3px solid var(--blue)', paddingLeft:16, marginTop:20 }}>{blog.excerpt}</p>
          )}
        </div>
      </div>

      {/* Article body */}
      <div style={{ padding:'56px 0 80px' }}>
        <div className="container" style={{ maxWidth:820 }}>
          <article className="blog-content" dangerouslySetInnerHTML={{ __html: blog.content }} />

          {/* Tools CTA */}
          <div style={{ background:'linear-gradient(135deg,var(--blue),var(--blue-dd))', borderRadius:'var(--r-lg)', padding:'32px 28px', marginTop:64, display:'flex', alignItems:'center', justifyContent:'space-between', gap:24, flexWrap:'wrap' }}>
            <div style={{ color:'white' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                <Icon name="tools" size={20} />
                <h3 style={{ fontWeight:700, fontSize:18 }}>Need a free tool?</h3>
              </div>
              <p style={{ fontSize:14, opacity:.85, lineHeight:1.55 }}>PDF tools, image tools — all free, no signup.</p>
            </div>
            <Link to="/tools" className="btn" style={{ background:'white', color:'var(--blue)', fontWeight:700, whiteSpace:'nowrap', flexShrink:0 }}>
              Explore Tools <Icon name="arrowRight" size={16} />
            </Link>
          </div>

          {/* Bottom nav */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:48, paddingTop:32, borderTop:'1.5px solid var(--slate-100)' }}>
            <Link to="/blog" className="btn btn-secondary"><Icon name="arrowLeft" size={16} />All Articles</Link>
            <button className="btn btn-secondary" onClick={() => { navigator.clipboard.writeText(window.location.href); }}>
              <Icon name="copy" size={16} />Copy Link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}